import type {
    AddCartItemPayload,
    Cart,
    DecreaseCartItemPayload,
    Order,
} from './types';

import { baseRequest } from '@/lib/base';

export const fetchActiveCart = () => baseRequest({
    url: '/order/carts/',
});

export const clearActiveCart = () => baseRequest({
    url: '/order/carts/clear/',
    method: 'POST',
});

export const addCartItem = (payload: AddCartItemPayload) => baseRequest({
    url: '/order/carts/items/add/',
    method: 'POST',
    body: payload,
});

export const decreaseCartItem = (payload: DecreaseCartItemPayload) => baseRequest({
    url: '/order/carts/items/decrease/',
    method: 'POST',
    body: payload,
});

export const removeCartItem = (productSlug: string) => baseRequest({
    url: `/order/carts/items/${productSlug}/`,
    method: 'DELETE',
});

export const createOrderFromCart = () => baseRequest({
    url: '/order/orders/',
    method: 'POST',
}) as Promise<{
    data: Order & Record<string, unknown>;
    ok: boolean;
    status: number;
    statusText: string;
}>;

export function extractCart(payload: unknown): Cart | null {
    if (!payload) return null;

    if (Array.isArray(payload)) {
        return (payload[0] as Cart | undefined) ?? null;
    }

    if (typeof payload !== 'object') return null;

    const obj = payload as Record<string, unknown>;

    if (Array.isArray(obj.results)) {
        return (obj.results[0] as Cart | undefined) ?? null;
    }

    if (obj.cart && typeof obj.cart === 'object') {
        return extractCart(obj.cart);
    }

    if (obj.data && typeof obj.data === 'object') {
        return extractCart(obj.data);
    }

    if (('uuid' in obj || 'id' in obj) && 'items' in obj) {
        return obj as unknown as Cart;
    }

    return null;
}

function zeroCartTotals(cart: Cart): Cart {
    return {
        ...cart,
        subtotal: '0',
        discount_total: '0',
        grand_total: '0',
    };
}

function updateCartPayload(
    payload: unknown,
    transformCart: (cart: Cart) => Cart | null,
): unknown {
    if (!payload) return payload;

    if (Array.isArray(payload)) {
        return payload
            .map((item) => {
                const cart = extractCart(item);
                return cart ? transformCart(cart) : item;
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    }

    if (typeof payload !== 'object') return payload;

    const obj = payload as Record<string, unknown>;
    const cart = extractCart(obj);

    if (cart && ('uuid' in obj || 'id' in obj) && 'items' in obj) {
        return transformCart(cart);
    }

    if (Array.isArray(obj.results)) {
        return {
            ...obj,
            results: obj.results
                .map((item) => {
                    const nestedCart = extractCart(item);
                    return nestedCart ? transformCart(nestedCart) : item;
                })
                .filter((item): item is NonNullable<typeof item> => item !== null),
        };
    }

    if (obj.cart && typeof obj.cart === 'object') {
        return {
            ...obj,
            cart: updateCartPayload(obj.cart, transformCart),
        };
    }

    if (obj.data && typeof obj.data === 'object') {
        return {
            ...obj,
            data: updateCartPayload(obj.data, transformCart),
        };
    }

    return payload;
}

export function removeCartItemFromCache(
    payload: unknown,
    productSlug: string,
) {
    return updateCartPayload(payload, (cart) => {
        const nextItems = cart.items.filter(
            (item) => item.product_slug !== productSlug,
        );

        if (nextItems.length === cart.items.length) {
            return cart;
        }

        const nextCart = {
            ...cart,
            items: nextItems,
        };

        return nextItems.length === 0
            ? zeroCartTotals(nextCart)
            : nextCart;
    });
}

export function clearCartCache(payload: unknown) {
    return updateCartPayload(payload, (cart) => zeroCartTotals({
        ...cart,
        items: [],
    }));
}

export function syncCartQueryData(
    current: unknown,
    nextPayload: unknown,
    status = 200,
    statusText = 'OK',
) {
    if (current && typeof current === 'object') {
        return {
            ...(current as Record<string, unknown>),
            data: nextPayload,
            ok: true,
            status,
            statusText,
        };
    }

    return {
        data: nextPayload,
        ok: true,
        status,
        statusText,
    };
}
